// Shared shell + option presets for the ECharts widgets. Every chart
// widget is: fetch -> rows -> option -> click events -> Card/hint/
// loading/empty guards -> <EChart>. The unique part of a widget is
// its data mapping and option; everything else lives here.
import React, { useContext, useMemo } from 'react'

import { EChart } from '@/components/charts/EChart'
import { Card } from '@/components/cards/Card'

import { EntityClickContext } from '../helpers/dashboardContexts'
import { WidgetActionHint } from '../components/WidgetActionHint'
import {
  buildDashboardParams,
  useDashboardOrgId,
  useFiltersForPickedScope,
} from '../helpers/dashboardParams'
import { useDashboardFetch } from '../hooks/useDashboardFetch'

// Card + hint + error/empty/loading guards around the chart. `children`
// renders between the hint and the chart (e.g. a summary line).
export const ChartWidgetCard = ({
  title,
  info,
  hint,
  hintKind = 'drawer',
  error,
  empty,
  emptyText,
  loading,
  option,
  events,
  height,
  children,
}) => (
  <Card title={title} info={info}>
    {hint && <WidgetActionHint kind={hintKind}>{hint}</WidgetActionHint>}
    {children}
    {error && <div className="entity-drawer-empty">{error}</div>}
    {!error && empty && !loading && <div className="entity-drawer-empty">{emptyText}</div>}
    {!error && !(empty && !loading) && (
      <EChart option={option} loading={loading} events={events} style={{ height }} />
    )}
  </Card>
)

// Standard data plumbing: picked-scope filters + org -> params -> fetch.
// `buildUrl(params, orgId)` returns the endpoint URL (params is a
// URLSearchParams primed with the dashboard filter dims).
export const useWidgetData = (scope, buildUrl) => {
  const filters = useFiltersForPickedScope(scope)
  const orgId = useDashboardOrgId()
  const url = orgId ? buildUrl(buildDashboardParams(filters, orgId), orgId) : null
  const fetched = useDashboardFetch(url, [orgId, filters])
  return { ...fetched, filters, orgId }
}

// Click handler reading an embedded payload off the data point. The
// EChart wrapper binds events ONCE at mount, so handlers must read
// p.data (never closures over widget state) or clicks silently no-op.
export const useEntityClickEvents = (dataKey, entityType) => {
  const clickEntity = useContext(EntityClickContext)
  return useMemo(
    () => ({
      click: (params) => {
        const payload = params?.data?.[dataKey]
        if (payload && clickEntity) {
          clickEntity({ type: entityType, payload })
        }
      },
    }),
    [dataKey, entityType, clickEntity]
  )
}

// Axis-triggered tooltip with the standard shadow pointer.
export const axisTooltip = (formatter) => ({
  trigger: 'axis',
  axisPointer: { type: 'shadow' },
  formatter,
})

// Horizontal-bar scaffold: grid + value xAxis + category yAxis with
// the house label sizing. Spread into the option, then add `series`.
export const hBarBase = (
  names,
  { left = 140, right = 32, top = 16, bottom = 24, xAxis = {} } = {}
) => ({
  grid: { left, right, top, bottom },
  xAxis: { type: 'value', axisLabel: { fontSize: 10 }, ...xAxis },
  yAxis: {
    type: 'category',
    data: names,
    inverse: true,
    axisLabel: { fontSize: 11, width: left - 10, overflow: 'truncate' },
  },
})
